import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { messages, type CompanyDetail, type CompanySummary } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

type Candidate = { company: CompanySummary; matchedBy: string; similarity: number; reason: string };

const FIELDS = [
  { key: 'name', label: '会社名' },
  { key: 'domain', label: 'ドメイン' },
  { key: 'industry', label: '業種' },
  { key: 'prefecture', label: '都道府県' },
  { key: 'city', label: '市区町村' },
] as const;

/** 重複統合（F05.3）。項目ごとに採用する値を選べる */
export const MergeDialog = ({
  open,
  onOpenChange,
  company,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyDetail;
}) => {
  const queryClient = useQueryClient();
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [selections, setSelections] = useState<Record<string, 'target' | 'source'>>({});

  const { data } = useQuery({
    queryKey: qk.companyDuplicates(company.id),
    queryFn: () => api.get<SingleResponse<Candidate[]>>(`/companies/${company.id}/duplicates`),
    enabled: open,
  });

  const source = data?.data.find((c) => c.company.id === sourceId);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ mergeId: string; carriedOver: { sends: number; replies: number; notes: number } }>>(
        `/companies/${company.id}/merge`,
        { sourceCompanyId: sourceId, fieldSelections: selections },
      ),
    onSuccess: (result) => {
      toast.success('企業を統合しました', {
        description: messages.company.mergeCarryOver(
          result.data.carriedOver.sends,
          result.data.carriedOver.replies,
          result.data.carriedOver.notes,
        ),
        duration: 10_000,
        action: {
          label: messages.company.mergeUndo,
          onClick: () => {
            void api.post(`/companies/merge/${result.data.mergeId}/undo`).then(() => {
              toast.success('統合を取り消しました');
              void queryClient.invalidateQueries({ queryKey: ['companies'] });
            });
          },
        },
      });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      onOpenChange(false);
      setSourceId(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lg:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{messages.company.mergeTitle}</DialogTitle>
          <DialogDescription>
            統合元の連絡先・送信履歴・返信・アポイント・タグ・メモを統合先に引き継ぎます。
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <p className="text-body font-semibold">統合する企業を選ぶ</p>
            {!data || data.data.length === 0 ? (
              <p className="text-body text-foreground-muted">重複候補はありません</p>
            ) : (
              <ul className="space-y-2">
                {data.data.map((candidate) => (
                  <li key={candidate.company.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-card border p-3',
                        sourceId === candidate.company.id
                          ? 'border-2 border-foreground bg-muted'
                          : 'border-border',
                      )}
                    >
                      <input
                        type="radio"
                        name="merge-source"
                        checked={sourceId === candidate.company.id}
                        onChange={() => setSourceId(candidate.company.id)}
                        className="mt-1 size-4 accent-foreground"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-body font-semibold">{candidate.company.name}</span>
                        <span className="block text-note text-foreground-muted">{candidate.reason}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {source && (
            <div className="space-y-2">
              <p className="text-body font-semibold">採用する値を選ぶ</p>
              <table className="w-full text-note">
                <thead>
                  <tr className="border-b border-border-strong">
                    <th scope="col" className="py-1.5 text-left">項目</th>
                    <th scope="col" className="py-1.5 text-left">{messages.company.mergeKeep}側</th>
                    <th scope="col" className="py-1.5 text-left">{messages.company.mergeSource}</th>
                  </tr>
                </thead>
                <tbody>
                  {FIELDS.map((field) => {
                    const targetValue = (company as unknown as Record<string, string | null>)[field.key];
                    const sourceValue = (source.company as unknown as Record<string, string | null>)[field.key];
                    const choice = selections[field.key] ?? 'target';
                    return (
                      <tr key={field.key} className="border-b border-border last:border-0">
                        <td className="py-2 text-foreground-muted">{field.label}</td>
                        <td className="py-2">
                          <label className="flex cursor-pointer items-center gap-1.5">
                            <input
                              type="radio"
                              name={`field-${field.key}`}
                              checked={choice === 'target'}
                              onChange={() =>
                                setSelections((prev) => ({ ...prev, [field.key]: 'target' }))
                              }
                              className="size-3.5 accent-foreground"
                            />
                            <span className="truncate">{targetValue ?? '（なし）'}</span>
                          </label>
                        </td>
                        <td className="py-2">
                          <label className="flex cursor-pointer items-center gap-1.5">
                            <input
                              type="radio"
                              name={`field-${field.key}`}
                              checked={choice === 'source'}
                              onChange={() =>
                                setSelections((prev) => ({ ...prev, [field.key]: 'source' }))
                              }
                              className="size-3.5 accent-foreground"
                            />
                            <span className="truncate">{sourceValue ?? '（なし）'}</span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-note font-semibold text-foreground">{messages.company.mergeUndoNote}</p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={!sourceId || mutation.isPending}>
            {messages.company.merge}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
